import React, { useMemo } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Radio from "antd/es/radio";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import { EntityType, EntityTypesSupportedByImport } from "@mapmycustomers/shared/types/entity";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";

import GecodeLimitInfo from "@app/component/GecodeLimitInfo";
import RadioGroup from "@app/component/input/RadioGroup";
import TextWithInfo from "@app/component/typography/TextWithInfo";
import TextWithLine from "@app/component/typography/TextWithLine";
import { selectEntityType } from "@app/scene/settings/component/Imports/store/actions";
import {
  getEntityType,
  getFilePreview,
} from "@app/scene/settings/component/Imports/store/selectors";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import getFeaturePathByEntityType from "@app/util/analytic/getFeaturePathByEntityType";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getEntityTypeDisplayName } from "@app/util/ui";

import FileUploader from "./FileUploader";

interface Props {
  entityType: EntityTypesSupportedByImport;
  selectEntityType: typeof selectEntityType;
}

const UploadStep: React.FC<Props> = ({ entityType, selectEntityType }) => {
  const intl = useIntl();

  const importAddAnalytics = useExtendedAnalytics(
    ["Settings", "Data", "New Import"],
    useAnalytics()
  );

  const uploadStepAnalyticIssuer = useMemo(
    () => importAddAnalytics.extend([...getFeaturePathByEntityType(entityType), "Step 1"]),
    [entityType, importAddAnalytics]
  );

  useChangeTracking(
    () =>
      uploadStepAnalyticIssuer.clicked([
        "Set Entity Type",
        ...getFeaturePathByEntityType(entityType),
      ]),
    [uploadStepAnalyticIssuer, entityType]
  );

  return (
    <div>
      <GecodeLimitInfo
        noLimitReachedTitle={intl.formatMessage({
          id: "settings.newImports.uploadStep.geocodingLimit.message",
          defaultMessage: "Importing large amounts of new data?",
          description: "Import first step page - no limit title",
        })}
      />
      <Row gutter={layout.spacerXL}>
        <Col span={24}>
          <TextWithLine>
            <TextWithInfo
              info={intl.formatMessage({
                id: "settings.newImports.uploadStep.selectRecordType.tooltip",
                defaultMessage:
                  "You may only upload with one record type (Company, People, Deals, Activities) during an import",
                description: "Import start step select record type tooltip",
              })}
            >
              {intl.formatMessage({
                id: "settings.newImports.uploadStep.selectRecordType",
                defaultMessage: "Select a record type to import:",
                description: "Import start step select record type text",
              })}
            </TextWithInfo>
          </TextWithLine>
          <RadioGroup buttonStyle="solid" onChange={selectEntityType} value={entityType}>
            {[EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL, EntityType.ACTIVITY].map(
              (entityType) => (
                <Radio.Button key={entityType} value={entityType}>
                  {getEntityTypeDisplayName(intl, entityType, { lowercase: false, plural: true })}
                </Radio.Button>
              )
            )}
          </RadioGroup>
          <FileUploader analyticIssuer={uploadStepAnalyticIssuer} />
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getEntityType(state),
  filePreview: getFilePreview(state),
});

const mapDispatchToProps = {
  selectEntityType,
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadStep);
